import React from "react";
import { columns } from "../../../../dtos/columns";
import { ResponseInformeDicomDto } from "../models";
import { MorososDicomKeyDto } from "../interfaces";
import { Checkbox } from '@mui/material';

const keyMatch = (a: MorososDicomKeyDto, b: MorososDicomKeyDto) =>
  a.rut === b.rut && a.nroOperacion === b.nroOperacion && a.nroDividendo === b.nroDividendo;

const rowToKey = (row: ResponseInformeDicomDto): MorososDicomKeyDto => ({
  rut: row.rut,
  nroOperacion: row.nroOperacion,
  nroDividendo: row.nroDividendo,
});

interface IProps {
  controlTabla: any;
  selectedKeys: MorososDicomKeyDto[];
  setSelectedKeys: React.Dispatch<React.SetStateAction<MorososDicomKeyDto[]>>;
  selectAll: boolean;
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useColumnsInformeDicom({ controlTabla, selectedKeys, setSelectedKeys, selectAll, setSelectAll }: IProps) {
  const pageRows: ResponseInformeDicomDto[] = controlTabla?.data ?? [];
  const allPageSelected =
    pageRows.length > 0 &&
    pageRows.every(row => selectedKeys.some(k => keyMatch(k, rowToKey(row))));

  const COLUMNS: columns<ResponseInformeDicomDto>[] = [
    {
      name: (
        <Checkbox
          size="small"
          checked={selectAll || allPageSelected}
          indeterminate={
            !selectAll &&
            !allPageSelected &&
            pageRows.some(row => selectedKeys.some(k => keyMatch(k, rowToKey(row))))
          }
          onChange={e => {
            if (e.target.checked) {
              const pageKeys = pageRows.map(rowToKey);
              setSelectedKeys(prev => [
                ...prev.filter(k => !pageKeys.some(pk => keyMatch(k, pk))),
                ...pageKeys,
              ]);
            } else {
              const pageKeys = pageRows.map(rowToKey);
              setSelectedKeys(prev => prev.filter(k => !pageKeys.some(pk => keyMatch(k, pk))));
              setSelectAll(false);
            }
          }}
        />
      ),
      selector: (row: ResponseInformeDicomDto) => row.nroOperacion,
      cell: (row: ResponseInformeDicomDto) => {
        const key = rowToKey(row);
        return (
          <Checkbox
            size="small"
            checked={selectAll || selectedKeys.some(k => keyMatch(k, key))}
            disabled={selectAll}
            onChange={e => {
              setSelectedKeys(prev => {
                if (e.target.checked) {
                  if (prev.some(k => keyMatch(k, key))) return prev;
                  return [...prev, key];
                }
                return prev.filter(k => !keyMatch(k, key));
              });
            }}
          />
        );
      },
      allowOverflow: true,
      button: true,
      export: false,
      width: '60px',
    },
    {
      name: "Tipo deudor",
      selector: (row) => row.tipoCliente,
      sortable: true,
      sortField: "tipoCliente",
      wrap: true,
      export: true,
    },
    {
      name: "N° Operación",
      selector: (row) => row.nroOperacion,
      sortable: true,
      sortField: "nroOperacion",
      wrap: true,
      export: true,
    },
    {
      name: "Rut",
      selector: (row) => row.rut,
      sortable: true,
      sortField: "rut",
      wrap: true,
      export: true,
    },
    {
      name: "Nombre",
      selector: (row) => row.nombres,
      sortable: true,
      sortField: "nombres",
      wrap: true,
      export: true,
    },
    {
      name: "Apellido Paterno",
      selector: (row) => row.apePaterno,
      sortable: true,
      sortField: "apePaterno",
      wrap: true,
      export: true,
    },
    {
      name: "Apellido Materno",
      selector: (row) => row.apeMaterno,
      sortable: true,
      sortField: "apeMaterno",
      wrap: true,
      export: true,
    },
    {
      name: "N° Dividendo",
      selector: (row) => row.nroDividendo,
      sortable: true,
      sortField: "nroDividendo",
      wrap: true,
      export: true,
    },
    {
      name: "Monto",
      selector: (row) => row.totalDividendo,
      sortable: true,
      sortField: "totalDividendo",
      wrap: true,
      export: true,
    },
  ];

  return { COLUMNS };
}
